import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaEye,
  FaBullseye,
  FaRocket,
} from "react-icons/fa";

import {
  HiLightBulb,
  HiAcademicCap,
  HiScale,
  HiUsers,
  HiUserGroup,
  HiCodeBracket,
  HiDocumentText,
  HiRocketLaunch,
  HiMicrophone,
  HiArrowRight,
} from "react-icons/hi2";

import { HiUserAdd } from "react-icons/hi";

/* =========================================================
   STUDENT CAREER JOURNEY
========================================================= */

const journey = [
  {
    icon: HiUserAdd,
    title: "Step 1: Join IA4Solutions",
    text: "Take your first step toward success — enroll in IA4Solutions and unlock structured career tracks curated by experts.",
    color: "from-blue-400 to-blue-600",
  },
  {
    icon: HiAcademicCap,
    title: "Step 2: Learn Trending Technologies",
    text: "Master in-demand skills like Python, Java, Web Development, Data Science, and Cloud Computing through guided mentorship.",
    color: "from-indigo-400 to-indigo-600",
  },
  {
    icon: HiLightBulb,
    title: "Step 3: Build Problem-Solving Skills",
    text: "Sharpen your logic and analytical thinking with coding challenges, hackathons, and real-world case studies.",
    color: "from-yellow-400 to-yellow-600",
  },
  {
    icon: HiCodeBracket,
    title: "Step 4: Develop Real Projects",
    text: "Apply your skills to build full-fledged projects — from idea to deployment — guided by mentors and industry standards.",
    color: "from-green-400 to-green-600",
  },
  {
    icon: HiUserGroup,
    title: "Step 5: Internships & Freelancing",
    text: "Gain hands-on experience through internships, freelance work, or collaborative projects that enhance your portfolio.",
    color: "from-teal-400 to-teal-600",
  },
  {
    icon: HiDocumentText,
    title: "Step 6: Build a Professional Resume & Profile",
    text: "Create an ATS-friendly resume, LinkedIn profile, and GitHub portfolio that reflect your skills and achievements.",
    color: "from-purple-400 to-purple-600",
  },
  {
    icon: HiMicrophone,
    title: "Step 7: Prepare for Interviews",
    text: "Get trained in mock interviews, aptitude, communication, and technical rounds to boost your confidence and readiness.",
    color: "from-pink-400 to-pink-600",
  },
  {
    icon: HiRocketLaunch,
    title: "Step 8: Step into the Industry",
    text: "Land your dream job with the support of our placement cell and network of hiring partners. Your journey begins here!",
    color: "from-red-400 to-red-600",
  },
];

/* =========================================================
   ABOUT PAGE
========================================================= */

const About = () => {
  return (
    <section className="relative py-20">
      <div className="container mx-auto px-3 space-y-6">

        {/* =====================================================
            PAGE HEADING
        ===================================================== */}

        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="
            relative
            text-center
            text-2xl
            font-bold
            text-blue-900
            dark:text-white
            md:text-4xl
          "
        >
          About ia4solutions

          <span
            aria-hidden="true"
            className="
              absolute
              bottom-[-8px]
              left-1/2
              h-1
              w-24
              -translate-x-1/2
              rounded-full
              bg-gradient-to-r
              from-blue-400
              to-indigo-500
            "
          />
        </motion.h1>

        {/* =====================================================
            INTRO
        ===================================================== */}

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          viewport={{ once: true }}
          className="
            mx-auto
            mt-6
            max-w-3xl
            text-center
            text-base
            leading-relaxed
            text-gray-700
            dark:text-gray-300
            md:text-lg
            lg:text-xl
          "
        >
          At{" "}
          <span className="font-semibold text-blue-800 dark:text-blue-400">
            Innovative Academy for IT Solutions (ia4s)
          </span>
          , we empower students, job seekers, and professionals with{" "}
          <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text font-medium text-transparent">
            in-demand IT skills
          </span>
          ,{" "}
          <span className="bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text font-medium text-transparent">
            hands-on projects
          </span>{" "}
          and{" "}
          <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text font-medium text-transparent">
            career-ready exposure
          </span>
          . Our goal is to bridge the gap between learning and success in
          today&apos;s digital-first world.
        </motion.p>

        {/* =====================================================
            VISION & MISSION
        ===================================================== */}

        <div className="grid gap-12 text-center md:grid-cols-2 md:text-left">
          {[
            {
              icon: FaEye,
              title: "Our Vision",
              text: "To lead in IT education and digital innovation, empowering individuals and businesses with future-ready skills and smart technology solutions.",
              color: "text-blue-600",
            },
            {
              icon: FaBullseye,
              title: "Our Mission",
              text: "To train and mentor tech talent through practical, industry-focused learning, helping both freshers and professionals thrive in the evolving IT industry.",
              color: "text-green-600",
            },
          ].map((item, i) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={i}
                whileHover={{ scale: 1.03 }}
                className="
                  rounded-2xl
                  border
                  border-blue-100
                  bg-white
                  p-6
                  shadow-lg
                  transition-shadow
                  hover:shadow-xl
                  dark:border-gray-700
                  dark:bg-gray-800
                "
              >
                <Icon
                  aria-hidden="true"
                  className={`mx-auto mb-4 text-3xl md:mx-0 ${item.color}`}
                />

                <h2 className="mb-4 text-2xl font-bold text-blue-900 dark:text-white">
                  {item.title}
                </h2>

                <p className="leading-relaxed text-gray-600 dark:text-gray-300">
                  {item.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* =====================================================
            CORE VALUES
        ===================================================== */}

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          viewport={{ once: true }}
          className="
            relative
            overflow-hidden
            rounded-3xl
            bg-gradient-to-r
            from-blue-50
            to-indigo-50
            p-8
            shadow-sm
            dark:from-gray-900
            dark:to-gray-800
            md:p-12
          "
        >
          {/* Decorative Shapes */}

          <div
            aria-hidden="true"
            className="
              animate-pulse-slow
              absolute
              -left-10
              -top-10
              h-36
              w-36
              rounded-full
              bg-blue-200
              opacity-10
              blur-2xl
              dark:bg-blue-700
            "
          />

          <div
            aria-hidden="true"
            className="
              animate-pulse-slow
              absolute
              -bottom-10
              -right-10
              h-52
              w-52
              rounded-full
              bg-indigo-200
              opacity-10
              blur-2xl
              dark:bg-indigo-700
            "
          />

          <div
            aria-hidden="true"
            className="
              animate-pulse-slow
              absolute
              right-12
              top-20
              h-32
              w-32
              rounded-full
              bg-purple-300
              opacity-10
              blur-2xl
              dark:bg-purple-700
            "
          />

          <div className="relative z-10 flex flex-col items-center gap-8 md:flex-row md:items-start">
            {/* Main Icon */}

            <div
              className="
                flex
                h-12
                w-12
                shrink-0
                items-center
                justify-center
                rounded-full
                bg-white
                shadow-xl
                transition-all
                duration-500
                hover:shadow-2xl
                dark:bg-gray-700
              "
            >
              <HiUsers
                aria-hidden="true"
                className="
                  text-xl
                  text-purple-600
                  transition-colors
                  duration-500
                  hover:text-indigo-500
                "
              />
            </div>

            {/* Content */}

            <div className="flex-1">
              <h2
                className="
                  relative
                  mb-8
                  text-center
                  text-3xl
                  font-bold
                  text-blue-900
                  dark:text-white
                  md:text-left
                  md:text-4xl
                "
              >
                Our Core Values

                <span
                  aria-hidden="true"
                  className="
                    absolute
                    bottom-[-8px]
                    left-1/2
                    h-1
                    w-24
                    -translate-x-1/2
                    rounded-full
                    bg-gradient-to-r
                    from-blue-400
                    to-indigo-500
                    md:left-0
                    md:translate-x-0
                  "
                />
              </h2>

              <div className="grid gap-6 text-left md:grid-cols-2">
                {[
                  {
                    icon: HiLightBulb,
                    text: "Innovation-driven learning",
                    color: "text-yellow-500",
                  },
                  {
                    icon: HiAcademicCap,
                    text: "Practical & industry-focused training",
                    color: "text-blue-500",
                  },
                  {
                    icon: HiScale,
                    text: "Integrity and transparency",
                    color: "text-green-600",
                  },
                  {
                    icon: HiUsers,
                    text: "Lifelong mentorship & guidance",
                    color: "text-pink-500",
                  },
                  {
                    icon: FaRocket,
                    text: "Career success as our priority",
                    color: "text-red-500",
                    full: true,
                  },
                ].map((val, i) => {
                  const Icon = val.icon;

                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.2 + i * 0.15,
                        type: "spring",
                        stiffness: 50,
                      }}
                      viewport={{ once: true }}
                      className={`
                        flex
                        items-start
                        gap-4
                        rounded-3xl
                        border
                        border-gray-100
                        bg-white
                        p-3
                        shadow-lg
                        transition-all
                        duration-500
                        hover:-translate-y-2
                        hover:shadow-2xl
                        dark:border-gray-700
                        dark:bg-gray-700
                        ${val.full ? "md:col-span-2" : ""}
                      `}
                    >
                      <div
                        className="
                          flex
                          h-10
                          w-10
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          bg-gray-100
                          transition-colors
                          duration-500
                          hover:bg-gray-200
                          dark:bg-gray-800
                          dark:hover:bg-gray-700
                        "
                      >
                        <Icon
                          aria-hidden="true"
                          className={`${val.color} text-xl transition-colors duration-500 hover:text-indigo-500`}
                        />
                      </div>

                      <p
                        className="
                          text-base
                          font-medium
                          leading-relaxed
                          text-gray-800
                          dark:text-gray-300
                          md:text-lg
                        "
                      >
                        {val.text
                          .split(" ")
                          .map((word, idx) =>
                            [
                              "Innovation-driven",
                              "Practical",
                              "Integrity",
                              "Lifelong",
                              "Career",
                            ].includes(word) ? (
                              <span
                                key={idx}
                                className="
                                  bg-gradient-to-r
                                  from-blue-400
                                  to-indigo-500
                                  bg-clip-text
                                  font-semibold
                                  text-transparent
                                "
                              >
                                {word}{" "}
                              </span>
                            ) : (
                              word + " "
                            )
                          )}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* =====================================================
            STUDENT CAREER JOURNEY
        ===================================================== */}

        <div className="relative overflow-hidden py-2 md:py-8">
          {/* Decorative Background */}

          <div
            aria-hidden="true"
            className="
              animate-blob
              absolute
              -left-10
              top-0
              h-72
              w-72
              rounded-full
              bg-blue-300/20
              blur-3xl
              dark:bg-blue-600/20
            "
          />

          <div
            aria-hidden="true"
            className="
              animate-blob
              animation-delay-4000
              absolute
              -bottom-10
              -right-10
              h-72
              w-72
              rounded-full
              bg-indigo-400/20
              blur-3xl
              dark:bg-indigo-700/20
            "
          />

          {/* Heading */}

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative z-10 px-4 text-center"
          >
            <h2
              className="
                mb-3
                text-2xl
                font-bold
                leading-tight
                md:text-3xl
                lg:text-4xl
              "
            >
              <span className="animate-gradient-x bg-gradient-to-r from-blue-500 via-indigo-600 to-teal-500 bg-clip-text text-transparent">
                Your Career Journey
              </span>

              <span className="mx-2 font-extrabold text-gray-800 dark:text-gray-200">
                at
              </span>

              <span className="animate-gradient-x delay-200 bg-gradient-to-r from-purple-600 via-pink-500 to-red-500 bg-clip-text text-transparent">
                ia4solutions
              </span>
            </h2>

            <p className="mx-auto max-w-3xl text-center text-base leading-relaxed text-gray-600 dark:text-gray-300 md:text-lg">
              <span className="font-semibold text-blue-600 dark:text-blue-400">
                Step-by-step roadmap
              </span>{" "}
              from{" "}
              <span className="font-medium text-indigo-600 dark:text-indigo-400">
                mastering new skills
              </span>{" "}
              <span className="relative inline-block">
                <span className="underline decoration-2 decoration-indigo-500 underline-offset-4 dark:decoration-indigo-400">
                  to landing your dream job
                </span>

                <span
                  aria-hidden="true"
                  className="
                    absolute
                    bottom-[-4px]
                    left-0
                    h-0.5
                    w-full
                    rounded
                    bg-gradient-to-r
                    from-indigo-400
                    via-purple-400
                    to-pink-400
                    opacity-30
                  "
                />
              </span>{" "}
              in the tech industry.
            </p>
          </motion.div>

          {/* Timeline */}

          <div className="relative mx-auto mt-14 max-w-6xl overflow-visible px-4 md:mt-16">
            {/* Desktop Progress Line */}

            <div
              aria-hidden="true"
              className="
                absolute
                left-1/2
                hidden
                h-full
                w-1
                -translate-x-1/2
                md:block
              "
            >
              <div
                className="
                  absolute
                  inset-0
                  rounded-full
                  bg-gradient-to-b
                  from-blue-400
                  via-indigo-500
                  to-purple-500
                  opacity-60
                  shadow-inner
                "
              />
            </div>

            <div className="space-y-8 md:space-y-0">
              {journey.map((step, i) => {
                const isLeft = i % 2 === 0;
                const StepIcon = step.icon;

                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: i * 0.15,
                      type: "spring",
                      stiffness: 60,
                    }}
                    viewport={{ once: true }}
                    className="
                      group
                      relative
                      md:grid
                      md:grid-cols-2
                      md:items-center
                      md:gap-12
                    "
                  >
                    {/* Timeline Number */}

                    <div
                      className="
                        absolute
                        left-1/2
                        top-0
                        z-20
                        hidden
                        -translate-x-1/2
                        -translate-y-1/2
                        md:block
                      "
                    >
                      <div
                        className="
                          flex
                          h-10
                          w-10
                          items-center
                          justify-center
                          rounded-full
                          bg-gradient-to-br
                          from-indigo-500
                          to-purple-500
                          font-bold
                          text-white
                          shadow-lg
                          md:h-12
                          md:w-12
                        "
                      >
                        {i + 1}
                      </div>
                    </div>

                    {/* Step Card */}

                    <div
                      className={`
                        mt-6
                        md:mt-0
                        ${isLeft
                          ? "md:col-start-1 md:order-1 md:text-right"
                          : "md:col-start-2 md:order-2 md:text-left"
                        }
                      `}
                    >
                      <div
                        className={`
                          rounded-2xl
                          bg-gradient-to-br
                          ${step.color}
                          p-1
                          shadow-lg
                          transition-all
                          duration-300
                          group-hover:shadow-2xl
                        `}
                      >
                        <div
                          className="
                            rounded-[15px]
                            bg-white/90
                            p-3
                            backdrop-blur-md
                            transition-all
                            duration-500
                            hover:-translate-y-1
                            hover:shadow-xl
                            dark:bg-gray-800/90
                          "
                        >
                          <div
                            className={`
                              flex
                              items-start
                              gap-4
                              ${isLeft
                                ? "md:flex-row-reverse"
                                : ""
                              }
                            `}
                          >
                            <div
                              className={`
                                flex
                                h-12
                                w-12
                                shrink-0
                                items-center
                                justify-center
                                rounded-full
                                bg-gradient-to-r
                                ${step.color}
                                text-white
                                shadow-md
                                transition-transform
                                duration-500
                                group-hover:rotate-6
                                group-hover:scale-110
                              `}
                            >
                              <StepIcon className="text-xl md:text-2xl" />
                            </div>

                            <div className="flex-grow">
                              <h3 className="text-sm font-bold text-blue-900 dark:text-white md:text-lg">
                                {step.title}
                              </h3>

                              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300 md:text-base">
                                {step.text}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Career Journey CTA */}

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="relative z-10 mt-16 text-center md:mt-20"
          >
            <Link to="/register">
              <button
                type="button"
                className="
                  group
                  inline-flex
                  items-center
                  justify-center
                  rounded-full
                  bg-gradient-to-r
                  from-blue-600
                  to-indigo-700
                  px-8
                  py-3
                  font-semibold
                  text-white
                  shadow-lg
                  transition-all
                  duration-300
                  hover:scale-105
                  hover:shadow-2xl
                "
              >
                <span className="flex items-center gap-2">
                  Start Your Journey Today

                  <HiArrowRight
                    aria-hidden="true"
                    className="
                      transition-transform
                      duration-300
                      group-hover:translate-x-2
                    "
                  />
                </span>
              </button>
            </Link>
          </motion.div>
        </div>

        {/* =========================================================
    TRAINERS & TEAM — PREMIUM PROFILE CARDS
========================================================= */}

        <div className="relative py-8 md:py-10">

          {/* =========================================================
      SOFT BACKGROUND
  ========================================================= */}

          <div
            aria-hidden="true"
            className="
      pointer-events-none
      absolute
      inset-0
      overflow-hidden
    "
          >
            <div
              className="
        absolute
        left-[15%]
        top-24
        h-56
        w-56
        rounded-full
        bg-blue-500/[0.035]
        blur-3xl
      "
            />

            <div
              className="
        absolute
        right-[10%]
        bottom-20
        h-64
        w-64
        rounded-full
        bg-indigo-500/[0.035]
        blur-3xl
      "
            />
          </div>

          {/* =========================================================
      HEADER
  ========================================================= */}

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
            className="
      relative
      z-10
      mx-auto
      mb-7
      max-w-3xl
      text-center
    "
          >
            {/* Eyebrow */}

            <div className="mb-2.5 flex items-center justify-center gap-3">
              <span
                aria-hidden="true"
                className="
          h-px
          w-8
          bg-gradient-to-r
          from-transparent
          to-blue-500
        "
              />

              <span
                className="
          text-[10px]
          font-bold
          uppercase
          tracking-[0.2em]
          text-blue-600
          dark:text-blue-400
          sm:text-xs
        "
              >
                Our People
              </span>

              <span
                aria-hidden="true"
                className="
          h-px
          w-8
          bg-gradient-to-l
          from-transparent
          to-blue-500
        "
              />
            </div>

            {/* Heading */}

            <h2
              className="
        text-2xl
        font-extrabold
        tracking-tight
        text-blue-900
        dark:text-white
        sm:text-3xl
        md:text-4xl
      "
            >
              Meet Our Trainers &amp; Team
            </h2>

            {/* Description */}

            <p
              className="
        mx-auto
        mt-2.5
        max-w-xl
        text-sm
        leading-6
        text-gray-600
        dark:text-gray-300
        sm:text-[15px]
      "
            >
              Meet the professionals behind our training, mentorship,
              technology programs, and student support.
            </p>
          </motion.div>

          {/* =========================================================
      PROFILE GRID
  ========================================================= */}

          <div
            className="
      relative
      z-10
      mx-auto
      grid
      max-w-6xl
      grid-cols-1
      gap-4
      lg:grid-cols-2
      lg:gap-5
    "
          >
            {[
              {
                name: "Kotakonda Sreekanth",
                role: "Managing Director, IA4Solutions",
                image: "/images/staff/Kotakonda_Sreekanth.png",
                message:
                  "Focused on quality teaching, motivation, and helping learners transform their skills into meaningful career opportunities through practical guidance and mentorship.",
                focus:
                  "Leadership • Mentorship • Career Development",
              },

              {
                name: "Vemula Rajesh",

                role: "Co-Founder • Technology Lead • Full Stack Developer • Technical Trainer",

                image: "/images/staff/vemula-rajesh.png",

                message:
                  "Co-Founder and technology professional specializing in full-stack development, data analytics, technical training, and industry-oriented skill development. Experienced in building modern web applications, designing technical curricula, delivering hands-on training across multiple technology domains, and guiding learners through real-time projects and practical implementation.",

                focus:
                  "Full Stack • Data Analytics • Python • SQL • Power BI • Tableau • Excel • Web Development • Technical Training • Curriculum Design • Real-Time Projects",
              },

              {
                name: "Tiruvathur Prasanna Kumar",
                role: "Senior Business Process Management Consultant",
                image: "/images/staff/Tiruvathur_Prasanna_Kumar.png",
                message:
                  "Bringing professional consulting experience into corporate training, student mentoring, placement coordination, presentations, and industry relationship building.",
                focus:
                  "Consulting • Corporate Training • Placement Coordination",
              },

              {
                name: "Pokala Varalakshmi",
                role: "DevOps Technical Trainer",
                image: "/images/staff/Pokala_Varalakshmi.png",
                message:
                  "Focused on hands-on DevOps and cloud learning through AWS, Linux, Git, Jenkins, Docker, Kubernetes, Terraform, Ansible, and CI/CD practices.",
                focus:
                  "AWS • Docker • Kubernetes • CI/CD",
              },

              {
                name: "M Anil",
                role: "Digital Marketer",
                image: "/images/staff/M_Anil.png",
                message:
                  "Helping learners develop practical digital marketing capabilities across SEO, social media, Google Ads, content, analytics, and AI-powered marketing.",
                focus:
                  "SEO • Google Ads • Social Media • AI Marketing",
              },

              {
                name: "Challa Priyanka",
                role: "HR Trainer",
                image: "/images/staff/Challa_Priyanka.png",
                message:
                  "Helping learners develop practical HR knowledge, professional communication, and the confidence required for real-world HR roles and career growth.",
                focus:
                  "HR Practices • Communication • Job Readiness",
              },

              {
                name: "Harshasree Kannemadugu",
                role: "Jr. Software Developer",
                image: "/images/staff/Harshasree_Kannemadugu.png",
                message:
                  "Supporting practical, project-based learning that encourages problem solving, collaboration, technology application, and industry-oriented skill development.",
                focus:
                  "Software Development • Projects • Industry Skills",
              },

              {
                name: "Dondapati Dhakshayani",
                role: "HR Admin",
                image: "/images/staff/Dondapati_Dhakshayani.png",
                message:
                  "Supporting students, admissions, batch coordination, trainer coordination, and academic operations to help create a smooth learning environment.",
                focus:
                  "Admissions • Coordination • Student Support",
              },
            ].map((member, index) => (
              <motion.article
                key={member.name}
                initial={{
                  opacity: 0,
                  y: 18,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  margin: "0px 0px -40px 0px",
                }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.05,
                  ease: "easeOut",
                }}
                className="
          group
          relative
          overflow-hidden
          rounded-2xl
          border
          border-gray-200
          bg-white
          shadow-sm
          transition-all
          duration-300
          hover:-translate-y-1
          hover:border-blue-200
          hover:shadow-[0_18px_45px_-22px_rgba(37,99,235,0.28)]
          dark:border-gray-700
          dark:bg-gray-900
          dark:hover:border-blue-800
        "
              >
                {/* =====================================================
            CARD TOP ACCENT
        ===================================================== */}

                <div
                  aria-hidden="true"
                  className="
            absolute
            left-0
            right-0
            top-0
            h-[2px]
            bg-gradient-to-r
            from-blue-500
            via-cyan-400
            to-indigo-500
            opacity-70
          "
                />

                {/* =====================================================
            PROFILE LAYOUT
        ===================================================== */}

                <div
                  className="
            grid
            grid-cols-[92px_minmax(0,1fr)]
            gap-4
            p-4
            sm:grid-cols-[110px_minmax(0,1fr)]
            sm:gap-5
            sm:p-5
          "
                >
                  {/* ===================================================
              PHOTO
          =================================================== */}

                  <div
                    className="
              relative
              flex
              h-[128px]
              w-[92px]
              items-end
              justify-center
              overflow-hidden
              rounded-xl
              border
              border-gray-200
              bg-gradient-to-b
              from-slate-100
              via-slate-100
              to-slate-200
              dark:border-gray-700
              dark:from-gray-800
              dark:via-gray-800
              dark:to-gray-900
              sm:h-[148px]
              sm:w-[110px]
            "
                  >
                    {/* Photo glow */}

                    <div
                      aria-hidden="true"
                      className="
                absolute
                bottom-[-30px]
                left-1/2
                h-28
                w-28
                -translate-x-1/2
                rounded-full
                bg-blue-500/10
                blur-2xl
              "
                    />

                    {/* Photo */}

                    <img
                      src={member.image}
                      alt={`${member.name}, ${member.role} at IA4Solutions`}
                      width="400"
                      height="400"
                      loading="lazy"
                      decoding="async"
                      className="
                relative
                z-10
                h-full
                w-full
                object-contain
                object-bottom
                transition-transform
                duration-500
                group-hover:scale-[1.035]
              "
                    />

                    {/* Photo corner marker */}

                    <span
                      aria-hidden="true"
                      className="
                absolute
                bottom-2
                left-2
                z-20
                h-1.5
                w-1.5
                rounded-full
                bg-emerald-500
                shadow-[0_0_0_3px_rgba(16,185,129,0.12)]
              "
                    />
                  </div>

                  {/* ===================================================
              CONTENT
          =================================================== */}

                  <div className="min-w-0">

                    {/* Name */}

                    <h3
                      className="
                text-base
                font-bold
                leading-tight
                text-gray-950
                dark:text-white
                sm:text-lg
              "
                    >
                      {member.name}
                    </h3>

                    {/* Role */}

                    <p
                      className="
                mt-1
                text-[10px]
                font-semibold
                uppercase
                leading-4
                tracking-[0.065em]
                text-blue-600
                dark:text-blue-400
                sm:text-[11px]
              "
                    >
                      {member.role}
                    </p>

                    {/* Divider */}

                    <div
                      aria-hidden="true"
                      className="
                mt-3
                h-px
                w-10
                bg-gradient-to-r
                from-blue-500
                to-cyan-400
              "
                    />

                    {/* Message */}

                    <div className="relative mt-2.5">
                      {/* Quote mark */}

                      <span
                        aria-hidden="true"
                        className="
                  pointer-events-none
                  absolute
                  -right-1
                  -top-5
                  select-none
                  text-4xl
                  font-serif
                  font-bold
                  leading-none
                  text-blue-500/[0.08]
                  dark:text-blue-400/[0.08]
                "
                      >
                        ”
                      </span>

                      <p
                        className="
                  line-clamp-4
                  text-xs
                  leading-5
                  text-gray-600
                  dark:text-gray-300
                  sm:text-[13px]
                  sm:leading-5.5
                "
                      >
                        {member.message}
                      </p>
                    </div>

                    {/* Focus */}

                    <div
                      className="
                mt-3
                rounded-lg
                bg-gray-50
                px-2.5
                py-2
                dark:bg-gray-800/80
              "
                    >
                      <p
                        className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.08em]
                  text-gray-400
                  dark:text-gray-500
                "
                      >
                        Expertise
                      </p>

                      <p
                        className="
                  mt-0.5
                  line-clamp-2
                  text-[10px]
                  font-medium
                  leading-4
                  text-gray-600
                  dark:text-gray-300
                "
                      >
                        {member.focus}
                      </p>
                    </div>
                  </div>
                </div>

                {/* =====================================================
            HOVER EDGE
        ===================================================== */}

                <div
                  aria-hidden="true"
                  className="
            pointer-events-none
            absolute
            bottom-0
            left-0
            h-px
            w-0
            bg-gradient-to-r
            from-blue-500
            to-cyan-400
            transition-all
            duration-500
            group-hover:w-full
          "
                />
              </motion.article>
            ))}
          </div>
        </div>

        {/* =====================================================
            FINAL CALL TO ACTION
        ===================================================== */}

        <div className="mt-12 text-center">
          <motion.a
            whileHover={{ scale: 1.05 }}
            href="/courses"
            className="
              group
              relative
              inline-flex
              items-center
              justify-center
              overflow-hidden
              rounded-full
              bg-gradient-to-r
              from-blue-600
              to-indigo-800
              px-8
              py-4
              text-base
              font-semibold
              text-white
              shadow-xl
              transition-transform
              duration-300
              hover:scale-105
              hover:shadow-2xl
              dark:from-blue-500
              dark:to-indigo-600
            "
          >
            {/* Hover Overlay */}

            <span
              aria-hidden="true"
              className="
                absolute
                inset-0
                h-full
                w-full
                rounded-full
                bg-white
                opacity-0
                transition-opacity
                duration-500
                group-hover:opacity-10
              "
            />

            {/* Button Content */}

            <span className="relative flex items-center gap-2">
              Join ia4solutions Today

              <HiArrowRight
                aria-hidden="true"
                className="
                  text-xl
                  transition-transform
                  duration-300
                  group-hover:translate-x-2
                "
              />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default About;